import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '@components/ui/Button/Button';
import { Modal } from '@components/ui/Modal';
import { mergeRefs } from '@utils/mergeRefs';
import styles from './DialogShell.module.css';

// Matches the modalExit animation in DialogShell.module.css. A timer rather than Modal's onExited,
// because jsdom never fires animationend and every caller's tests wait for the close to land.
const EXIT_DURATION_MS = 200;

export interface IDialogAction {
  label: string;
  /** Receives `close`, which plays the exit animation and then calls the shell's `onClose`. */
  onClick: (close: () => void) => void;
  disabled?: boolean;
  busy?: boolean;
  busyLabel?: string;
}

/** ...rest and the ref go to the role="dialog" node; className goes to the panel. */
export interface IDialogShellProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title' | 'onClick' | 'role' | 'aria-modal'
> {
  isOpen: boolean;
  /** Called once the exit animation has finished, not when it starts. */
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  primaryAction: IDialogAction;
  secondaryAction?: IDialogAction;
  children?: React.ReactNode;
}

const DialogShell = forwardRef<HTMLDivElement, IDialogShellProps>(
  (
    {
      isOpen,
      onClose,
      title,
      subtitle,
      primaryAction,
      secondaryAction,
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const titleId = useId();
    const subtitleId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);
    const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onCloseRef = useRef(onClose);
    const [isExiting, setIsExiting] = useState(false);

    onCloseRef.current = onClose;

    const close = () => {
      if (exitTimerRef.current) return;
      setIsExiting(true);
      exitTimerRef.current = setTimeout(() => {
        exitTimerRef.current = null;
        setIsExiting(false);
        onCloseRef.current();
      }, EXIT_DURATION_MS);
    };

    useEffect(() => {
      if (!isOpen) return;
      return () => {
        if (exitTimerRef.current) {
          clearTimeout(exitTimerRef.current);
          exitTimerRef.current = null;
        }
        setIsExiting(false);
      };
    }, [isOpen]);

    if (!isOpen) return null;

    const renderAction = (action: IDialogAction, variant: 'inverse' | 'secondary', cls: string) => (
      <Button
        variant={variant}
        nativeFont
        className={cls}
        onClick={() => action.onClick(close)}
        disabled={action.disabled || action.busy}
        aria-busy={action.busy || undefined}
      >
        {action.busy && action.busyLabel ? action.busyLabel : action.label}
      </Button>
    );

    return createPortal(
      <>
        <div className={`${styles.backdrop} ${isExiting ? styles.backdropExiting : ''}`} />
        <div className={`${styles.gradient} ${isExiting ? styles.gradientExiting : ''}`} />
        <Modal
          {...rest}
          ref={mergeRefs(dialogRef, ref)}
          isOpen={isOpen}
          onClose={close}
          variant="dialog"
          layer="overlay-content"
          backdropClassName={styles.layer}
          portal={false}
          initialFocusRef={dialogRef}
          tabIndex={-1}
          aria-labelledby={titleId}
          aria-describedby={subtitle ? subtitleId : undefined}
          className={`${styles.modal} ${isExiting ? styles.modalExiting : ''} ${className ?? ''}`}
        >
          <Button
            variant="text"
            iconOnly
            nativeFont
            className={styles.close}
            onClick={close}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M14 4L4 14M4 4L14 14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </Button>

          <div className={styles.modalHeader}>
            <h2 id={titleId} className={styles.modalTitle}>
              {title}
            </h2>
            {subtitle && (
              <p id={subtitleId} className={styles.modalSubtitle}>
                {subtitle}
              </p>
            )}
          </div>

          {children}

          <div className={styles.actions}>
            {renderAction(primaryAction, 'inverse', styles.primaryButton)}
            {secondaryAction && renderAction(secondaryAction, 'secondary', styles.secondaryButton)}
          </div>
        </Modal>
      </>,
      document.body,
    );
  },
);

DialogShell.displayName = 'DialogShell';

export default DialogShell;
